import { useQuery } from "react-query";
import { AccessType, ColumnType, ConfigurationType, DateRangeType, DurationTimeframeTypeEnum, Scope, SortOrder, Style, TimeframeType } from "../../common/enums/DRCXEnums";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { NumericalFunctionEnum } from "../../common/enums/NumericalFunctionEnum";
import { AccessPermission } from "../../common/models/AccessPermission";
import { IDateRange } from "../../common/models/IDateRange";
import { DRCXConfigurationModelBase } from "../../common/models/drcx/DRCXConfigurationModelBase";
import { IDRCXConfigurationSectionObjectDTO } from "../../common/models/drcx/DRCXConfigurationSectionObjectDTO";
import { IeLynxObject } from "../../common/models/eLynxObject";
import UUID from "../../common/utilities/Uuid";
import i18n from "../../services/localization-service/i18n";
import { DEFAULT_STALE_TIME } from "../../services/query-client-service/QueryClientService";
import { RestError } from "../../services/rest-service/RestService";
import { ITrendConfigurationDTO, TrendConfigurationModel, TrendConfigurationSectionModel } from "../../services/trending-configuration-data-service/Models";
import { ITrendAdHocDataRequestModel, ITrendDataResultsModel, ITrendingDisplayOptions } from "../../services/trending-data-service/Models";
import TrendingDataService from "../../services/trending-data-service/TrendingDataServices";
import { UIChartPropertiesClient } from "../../services/trending-data-service/UIChartProperties";
import { BaseUseQueryParameters, DEFAULT_CACHE_TIME, DEFAULT_KEEP_PREVIOUS_DATA } from "../BaseUseQueryParameters";
import { keys } from "./Keys";

export type UseTankBatteryGraphDataQueryParams = {
    contextGroupID: number;
    contextObject: IeLynxObject;
    displayOptions: ITrendingDisplayOptions;
    dateRange: IDateRange;
    windowHeight: number;
    windowWidth: number;
} & BaseUseQueryParameters<ITrendDataResultsModel>;

export function useTankBatteryGraphData({
    contextGroupID,
    contextObject,
    displayOptions,

    dateRange,
    windowHeight,
    windowWidth,

    cacheTime,
    enabled,
    keepPreviousData,
    onError,
    onSuccess,
    refetchOnWindowFocus,
    staleTime
}: UseTankBatteryGraphDataQueryParams) {
    return useQuery<ITrendDataResultsModel | undefined, RestError>(

        keys.graphData(
            contextGroupID,
            contextObject,
            dateRange,
            displayOptions
        ),
        ({ signal }) => {

            const dataRequestOptions: ITrendAdHocDataRequestModel = {
                disableDeferredData: false,
                displayOptions: displayOptions,
                endTime: dateRange.endDate,
                startTime: dateRange.startDate,
                trendConfigurationModel: getTankDetailsTrendingConfig(
                    contextObject,
                    undefined,
                    [LiquidProductionTATEnum.TopLevel],
                    i18n.t("liquidProd.topLevels")
                ),
                windowHeight: windowHeight,
                windowWidth: windowWidth
            };

            return TrendingDataService.getDataAdHoc2(
                contextGroupID,
                contextObject,
                dataRequestOptions,
                signal
            );
        },
        {
            cacheTime: cacheTime ?? DEFAULT_CACHE_TIME,
            enabled: enabled,
            keepPreviousData: keepPreviousData ?? DEFAULT_KEEP_PREVIOUS_DATA,
            onError: onError,
            onSuccess: onSuccess,
            refetchInterval: ((data) => data?.refreshEnabled && data?.refreshSeconds
                ? data.refreshSeconds * 1000
                : false
            ),
            refetchOnWindowFocus: refetchOnWindowFocus,
            staleTime: staleTime ?? DEFAULT_STALE_TIME,
        }
    );
}

function getTankDetailsTrendingConfig(
    contextObject: IeLynxObject,
    sectionObjects: Array<IDRCXConfigurationSectionObjectDTO> | undefined,
    tankTats: Array<number>,
    singleSectionName: string
): ITrendConfigurationDTO {

    return {
        id: DRCXConfigurationModelBase.NEW_CONFIGURATION_ID,
        name: "New Graph",
        configurationType: ConfigurationType.Trending,
        access: AccessType.Private,
        userPermissions: new Array<AccessPermission>(),
        scope: Scope.User,
        created: new Date(),
        forAllGroups: true,
        overrideInstanceDates: true,
        timeframe: {
            durationTimeframeType: DurationTimeframeTypeEnum.MultipleTimeFrames,
            calculatedStartDateOffset: {
                duration: -30,
                rangeType: DateRangeType.Day
            },
            duration: {
                duration: 31,
                rangeType: DateRangeType.Day
            }
        },
        trendConfigurationType: TrendConfigurationModel.getConfigurationType(contextObject.objectType),
        sections: [
            getTankDetailsTrendingDefaultSection(sectionObjects, tankTats, singleSectionName)
        ],

        autoRefresh: false,
        combineAcrossDeviceAxes: false,
        displayAlarms: false,
        displayDataQualityEvents: false,
        displayDiagnosticEvents: false,
        displayManualEvents: false,
        displayLegend: true,
        displayMemos: false,
        displaySectionsStacked: false,
        displayTabularData: false,
        displaySettingsChanges: false,
        displaySimulationDataQualityEvents: false,
        displaySimulationDiagnosticEvents: false,
        hasComparisonTimeframe: false,
        uiChartProperties: new UIChartPropertiesClient()
    };
}

function getTankDetailsTrendingDefaultSection(
    sectionObjects: Array<IDRCXConfigurationSectionObjectDTO> | undefined,
    tankTats: Array<number>,
    singleSectionName: string
): TrendConfigurationSectionModel {

    let columnOrder: number = 0;
    //let objectOrder: number = 0;
    let sectionOrder: number = 0;

    //// All Tats
    //let tankTats: Array<number> = Enumerable.from(Enums.LiquidProductionTATEnum).select(item =>
    //    parseInt(item.key))
    //    .where(item => item != undefined && !isNaN(item))
    //    .orderBy(item => item)
    //    .toArray();

    //let tankTats: Array<number> = [Enums.LiquidProductionTATEnum.TopLevel, Enums.LiquidProductionTATEnum.InterfaceLevel];

    const newSection: TrendConfigurationSectionModel = new TrendConfigurationSectionModel({
        name: singleSectionName, //Utilities.StringFormat(i18n.translate("common.configuration.newSection"), 0),
        order: sectionOrder,
        // Trending: always uses Linear for the Style.
        style: Style.Linear,
        dateSortOrder: SortOrder.Ascending,
        deviceSortOrder: SortOrder.Ascending,
        timeframe: {
            fixedStartDate: undefined,
            fixedEndDate: undefined,
            runToCurrentDate: false,
            calculatedStartDateOffset: {
                duration: 0,
                rangeType: DateRangeType.Invalid
            },
            duration: {
                duration: 0,
                rangeType: DateRangeType.Invalid
            },
            currentDateOffset: {
                duration: 0,
                rangeType: DateRangeType.Invalid
            },
            durationTimeframeType: undefined,
        },
        acrossDevices: false,
        includeOilTanks: false,
        includeWaterTanks: false,
        plotSeriesOnSameYAxis: true,
        // Trending: Seems to always be set to true
        useTatEffectiveDate: true,
        sectionUniqueID: UUID.generate(),
        columns: tankTats.map(tat => {
            return {
                columnType: ColumnType.Tat,
                groupingFunction: NumericalFunctionEnum.Average,
                groupingTimeframe: TimeframeType.Hourly,
                order: columnOrder++,
                processInDB: true,
                tagAssociationTypeID: tat
            };
        }),
        objects: sectionObjects
    });

    return newSection;
}